import { SamplesQuery, Sample } from '@texas/api/endpoints/samplesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { productApprovalEvents } from '@texas/components/shared/productApproval/events';
import { sampleEvents } from '@texas/components/shared/samples/events';
import {
  SampleLifecycle,
  SampleLifecycleGroup,
  lifecycleToGroup,
  sampleLifecycleGroupConfig,
} from '@texas/components/shared/samples/types';
import { useMultiSelectSamples } from '@texas/components/shared/samples/useMultiSelectSamples';
import { shipmentEvents } from '@texas/components/shared/shipment/events';
import { trackerEvents } from '@texas/components/shared/tracker/events';
import { transportRequestEvents } from '@texas/components/shared/transportRequests/events';
import { useLocalStorage } from '@texas/hooks/useLocalStorage';
import { useMemo, useState, useEffect } from 'react';
import { EntityType, trackerApi } from '@texas/api/endpoints/trackerApi';
import { HttpClientResponse } from '@texas/api/httpClient/types';
import { SamplesOptions, WidgetLayout } from '../shared/types';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';

type SampleData = HttpClientResponse<Sample[]>;

type ArgsNullable = [
  pointer: number | null,
  entityType: EntityType | null,
  query: SamplesQuery,
];
type ArgsNonNullable = [
  pointer: number,
  entityType: EntityType,
  query: SamplesQuery,
];

function createSamplesFetcher(
  usePointer: boolean,
): (...args: ArgsNullable | ArgsNonNullable) => Promise<SampleData> {
  if (usePointer) {
    return ((...args: ArgsNonNullable) =>
      trackerApi.getSamplesFromPointer(...args)) as (
      ...args: ArgsNullable | ArgsNonNullable
    ) => Promise<SampleData>;
  } else {
    return ((...args: ArgsNullable) => trackerApi.getSamples(...args)) as (
      ...args: ArgsNullable | ArgsNonNullable
    ) => Promise<SampleData>;
  }
}

export function useSamplesWidget({
  options,
  widgetId,
}: {
  options?: SamplesOptions;
  widgetId: string;
}) {
  const fetcher = useMemo(
    () => createSamplesFetcher(options?.trackerPointer !== undefined),
    [options?.trackerPointer],
  );
  const { data, refetch, loading, error } = useApiResource(fetcher);

  const [searchQuery, setSearchQuery] = useState<SamplesQuery>({
    sortBy: 'sampleInitiatedDate',
    sortDesc: true,
  });

  const [layout, setLayout] = useLocalStorage(
    'samplesWidgetLayout',
    'table' as WidgetLayout,
  );

  useEffect(() => {
    if (!options?.trackerPointer || !options.entityType) {
      refetch(null, null, searchQuery);
      return;
    }
    refetch(options.trackerPointer, options.entityType, searchQuery);
  }, [options?.entityType, options?.trackerPointer, refetch, searchQuery]);

  useSignalSubscriptionEvents({
    triggerOnEvent: () => {
      refetch(
        options?.trackerPointer ?? null,
        options?.entityType ?? null,
        searchQuery,
      );
    },
    signalEvents: [
      sampleEvents.onSampleSentFromSupplier,
      sampleEvents.onSampleReceivedFromSupplier,
      sampleEvents.onSampleReadyDateUpdated,
      transportRequestEvents.onSamplesAdded,
      transportRequestEvents.onSamplesRemoved,
      transportRequestEvents.onTransportUpdated,
      shipmentEvents.onShipmentDispatched,
      shipmentEvents.onShipmentUnpacked,
      productApprovalEvents.productApprovalRejected,
      productApprovalEvents.productApprovalAccepted,
      productApprovalEvents.productApprovalsCreated,
      trackerEvents.onAdded,
      trackerEvents.onRemoved,
      sampleEvents.onSamplesLost,
      sampleEvents.onSamplesUpdated,
      sampleEvents.onSamplesActionPerformed,
    ],
  });

  const useMultiSelect = useMultiSelectSamples(options?.hideStopFollowing);
  const { filterBySamples } = useMultiSelect;

  useEffect(() => {
    filterBySamples(
      data?.map((d) => ({
        groupId: d.compositionGroupId,
        groupArchived: d.compositionGroupArchived,
        variantId: d.variantId,
        supplierId: d.supplierId,
        articleFolderId: d.articleFolderId,
        branchId: d.branchId,
        sample: {
          sampleReadyDate: d.sampleReadyDate,
          productApproval: d.productApproval,
          sampleId: d.sampleId,
          receivedFromSupplierUnknownShipment:
            d.receivedFromSupplierUnknownShipment,
          state: d.state,
          transport: d.transport,
          lifecycle: d.lifecycle,
          inPossessionOfUserId: d.inPossessionOfUser.id,
        },
      })) ?? [],
    );
  }, [data, filterBySamples]);

  const sampleGroups = useMemo(() => {
    const uniqueGroups: Record<SampleLifecycleGroup, Sample[]> = {
      [SampleLifecycleGroup.UnderwayFromSupplier]: [],
      [SampleLifecycleGroup.ReceivedFromSupplier]: [],
      [SampleLifecycleGroup.UnhandledSamples]: [],
      [SampleLifecycleGroup.InBrandTransport]: [],
      [SampleLifecycleGroup.InInternalTransport]: [],
      [SampleLifecycleGroup.ReceivedAtBrand]: [],
      [SampleLifecycleGroup.HandledSamples]: [],
    };

    if (!data) {
      return uniqueGroups;
    }

    data.forEach((x) => {
      const group =
        lifecycleToGroup[ensureEnumNumber(SampleLifecycle, x.lifecycle)];
      uniqueGroups[group].push(x);
    });

    return uniqueGroups;
  }, [data]);

  const groupConfigs = useMemo(() => {
    return Object.keys(sampleLifecycleGroupConfig);
  }, []);

  return {
    groupConfigs,
    sampleGroups,
    loading,
    layout,
    setLayout,
    searchQuery,
    setSearchQuery,
    error,
    useMultiSelect,
    hideStopFollowing: options?.hideStopFollowing,
    widgetId,
    persistColumnVisibility: options?.persistColumnVisibility,
  };
}
